import { FocusEvent, useCallback, useEffect, useRef } from "react";
import { isFocusVisible } from "@react-aria/interactions";
import { useShallow } from "zustand/react/shallow";

import useEscapeKeypress from "hooks/useEscapeKeyPress";
import useTypedContext from "hooks/useTypedContext";
import { ThemeContext } from "views/Theme";

import useMainNavigationStore from "../stores/useMainNavigationStore";
import useMainNavigationHoverWithDelay from "./useHoverWithDelay";

const HOVER_EFFECT_OPEN_DELAY = 150;
const HOVER_EFFECT_CLOSE_DELAY = 600;

/**
 * Reveals the collapsed sidebar as an overlay while the pointer (or keyboard focus) is over it.
 */
const useMainNavigationSidebarPreview = () => {
  const { reducedMotion } = useTypedContext(ThemeContext);

  const { isSidebarExpanded, isSidebarInPreview, mobileFullScreen, setIsSidebarInPreview } =
    useMainNavigationStore(
      useShallow((state) => ({
        isSidebarExpanded: state.isSidebarExpanded,
        isSidebarInPreview: state.isSidebarInPreview,
        mobileFullScreen: state.mobileFullScreen,
        setIsSidebarInPreview: state.setIsSidebarInPreview,
      }))
    );

  const ref = useRef<HTMLDivElement>(null);
  const isHoveredRef = useRef(false);
  const isFocusedRef = useRef(false);
  const wasFocusInsideRef = useRef(false);
  const isRestoringFocusRef = useRef(false);

  const handleOpen = useCallback(() => {
    setIsSidebarInPreview(true);
  }, [setIsSidebarInPreview]);

  const handleClose = useCallback(() => {
    wasFocusInsideRef.current = !!ref.current?.contains(document.activeElement);
    setIsSidebarInPreview(false);
  }, [setIsSidebarInPreview]);

  // Closing unmounts preview-only elements (pin buttons, inline group items); if one
  // held focus, the browser drops it to <body> and a keyboard user would restart
  // tabbing from the top of the page. Land focus back on the nav container instead.
  useEffect(() => {
    if (isSidebarInPreview || !wasFocusInsideRef.current) {
      return;
    }
    wasFocusInsideRef.current = false;
    if (!ref.current?.contains(document.activeElement)) {
      isRestoringFocusRef.current = true;
      ref.current?.focus({ preventScroll: true });
      isRestoringFocusRef.current = false;
    }
  }, [isSidebarInPreview]);

  const canPreview = !isSidebarExpanded && !mobileFullScreen;

  const { handleMouseEnter, handleMouseLeave, cancelPending } = useMainNavigationHoverWithDelay({
    onOpen: handleOpen,
    onClose: handleClose,
    shouldDisableOpen: !canPreview,
    openDelay: reducedMotion ? 0 : HOVER_EFFECT_OPEN_DELAY,
    closeDelay: reducedMotion ? 0 : HOVER_EFFECT_CLOSE_DELAY,
  });

  const handleEscape = useCallback(() => {
    isHoveredRef.current = false;
    cancelPending();
    handleClose();
  }, [cancelPending, handleClose]);

  useEscapeKeypress(isSidebarInPreview, handleEscape);

  const closeWhenIdle = () => {
    if (!isHoveredRef.current && !isFocusedRef.current) {
      handleMouseLeave();
    }
  };

  const onMouseEnter = () => {
    isHoveredRef.current = true;
    handleMouseEnter();
  };

  const onMouseLeave = () => {
    isHoveredRef.current = false;
    closeWhenIdle();
  };

  const onMouseMove = () => {
    if (isHoveredRef.current) {
      return;
    }
    isHoveredRef.current = true;
    handleMouseEnter();
  };

  const onFocus = (e: FocusEvent) => {
    // The programmatic focus restore after close must not count as user focus —
    // under keyboard modality it would pass the checks below and reopen the preview.
    if (isRestoringFocusRef.current) {
      return;
    }
    // Dropdown portals autofocus their menu on open; that focus lives outside the
    // nav's DOM and must not hold the preview (the menu can unmount without a blur).
    if (ref.current && !ref.current.contains(e.target)) {
      return;
    }
    // Only keyboard focus should hold the preview open — mouse clicks also focus the
    // clicked item, which would keep the preview stuck after the pointer left. React-aria
    // focuses pressed items programmatically, which can fool CSS :focus-visible, so ask
    // react-aria's modality tracker as well.
    if (!isFocusVisible() || !e.target.matches(":focus-visible")) {
      return;
    }
    isFocusedRef.current = true;
    // Clears any pending close; the hover open delay is pointless for keyboard,
    // so open right away — the pin button must be reachable without waiting.
    handleMouseEnter();
    if (canPreview) {
      handleOpen();
    }
  };

  const onBlur = () => {
    isFocusedRef.current = false;
    // isHoveredRef can go stale: a dropdown portal unmounting under the pointer swallows
    // the nav's mouseleave. Re-read the truth from the DOM — but not while a dropdown is
    // open: the pointer being in its portal counts as hovering even though :hover is false.
    if (!useMainNavigationStore.getState().activeDropdownGroupId) {
      isHoveredRef.current = !!ref.current?.matches(":hover");
    }
    closeWhenIdle();
  };

  return { ref, onMouseEnter, onMouseLeave, onMouseMove, onFocus, onBlur };
};

export default useMainNavigationSidebarPreview;
